import moment from 'moment';
import { isEmpty, reject, trim } from 'lodash';

import { OBJECTIVE_DATE_FORMAT } from './objectives.constants';
import { ObjectiveCategoryEnum, ObjectiveTypeEnum, KraMetricsEnum } from './objectives.enum';
import {
  CreateKraRequest,
  CreateMilestoneRequest,
  CreateObjectiveRequest,
  ObjectiveForm,
  ObjectiveResponse,
} from './types/objective.type';

export const emptyKra = (): CreateKraRequest => ({
  title: '',
  startValue: '',
  targetValue: '',
  metrics: '',
  targetDate: '',
  currentValue: '',
});

export const emptyMilestone = (targetDate: string): CreateMilestoneRequest => ({
  title: '',
  targetDate,
});

/**
 * The form starts with one blank KRA/milestone row, and users can add more than
 * they end up filling in. Blank rows are neither validated nor submitted.
 */
export const isBlankKra = (kra?: CreateKraRequest) =>
  !trim(kra?.title) && !trim(kra?.startValue) && !trim(kra?.targetValue) && !kra?.metrics;

export const isBlankMilestone = (milestone?: CreateMilestoneRequest) =>
  !trim(milestone?.title);

export const isValidObjectiveDate = (date?: string | null) =>
  !!date && moment(date, OBJECTIVE_DATE_FORMAT, true).isValid();

export const objectiveDurationInMonths = (startDate: string, endDate: string) =>
  moment(endDate, OBJECTIVE_DATE_FORMAT).diff(
    moment(startDate, OBJECTIVE_DATE_FORMAT),
    'months',
    true,
  );

export const getOwnerDisplayName = (
  ownerId: string | null | undefined,
  ownerName: string | null | undefined,
  loggedInProfileId: string | null | undefined,
) => (ownerId && ownerId === loggedInProfileId ? 'You' : ownerName);

export const getObjectiveCycleDates = () => ({
  startDate: moment().startOf('quarter').format(OBJECTIVE_DATE_FORMAT),
  endDate: moment().endOf('quarter').format(OBJECTIVE_DATE_FORMAT),
});

export const getObjectiveFormDefaultValues = (
  type: ObjectiveTypeEnum = ObjectiveTypeEnum.PERFORMANCE,
): CreateObjectiveRequest => {
  const { startDate, endDate } = getObjectiveCycleDates();

  return {
    title: '',
    description: '',
    category: ObjectiveCategoryEnum.INDIVIDUAL,
    type,
    startDate,
    endDate,
    parentId: '',
    kras: [emptyKra()],
    milestones: [emptyMilestone(endDate)],
  };
};

export const getExistingObjectiveFormDefaultValues = (
  objective: ObjectiveResponse,
): ObjectiveForm => {
  const endDate = moment(objective.endDate).format(OBJECTIVE_DATE_FORMAT);

  return {
    title: objective.title,
    description: objective.description,
    category: objective.category,
    type: objective.type,
    startDate: moment(objective.startDate).format(OBJECTIVE_DATE_FORMAT),
    endDate,
    parentId: objective.parentId || '',
    kras: isEmpty(objective.kras)
      ? [emptyKra()]
      : objective.kras.map((kra) => ({
          id: kra.id,
          title: kra.title,
          startValue: kra.startValue,
          targetValue: kra.targetValue,
          metrics: kra.metrics as KraMetricsEnum | '',
          targetDate: moment(kra.targetDate).format(OBJECTIVE_DATE_FORMAT),
        })),
    milestones: isEmpty(objective.milestones)
      ? [emptyMilestone(endDate)]
      : objective.milestones.map((milestone) => ({
          id: milestone.id,
          title: milestone.title,
          targetDate: moment(milestone.targetDate).format(OBJECTIVE_DATE_FORMAT),
        })),
  };
};

export const transformCreateObjectiveRequest = (
  form: CreateObjectiveRequest,
): CreateObjectiveRequest => {
  const kras = reject(form.kras, isBlankKra).map((kra) => ({
    id: kra.id,
    title: trim(kra.title),
    startValue: trim(kra.startValue) || '0',
    targetValue: trim(kra.targetValue),
    metrics: kra.metrics,
    // KRAs have no date of their own on the form, so they inherit the objective's end date
    targetDate: form.endDate,
  }));

  const milestones = reject(form.milestones, isBlankMilestone).map((milestone) => ({
    id: milestone.id,
    title: trim(milestone.title),
    targetDate: milestone.targetDate,
  }));

  return {
    title: trim(form.title),
    description: trim(form.description),
    category: form.category,
    type: form.type,
    startDate: form.startDate,
    endDate: form.endDate,
    parentId: form.parentId || undefined,
    kras: isEmpty(kras) ? undefined : kras,
    milestones: isEmpty(milestones) ? undefined : milestones,
  };
};
